import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, useCart, API } from "../App";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { Clock, Trash2, Play, ShoppingCart } from "lucide-react";
import { format } from "date-fns";

export default function OnHoldSales() {
  const { getAuthHeader } = useAuth();
  const { clearCart, addToCart } = useCart();
  const navigate = useNavigate();
  const [heldSales, setHeldSales] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSale, setSelectedSale] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  useEffect(() => {
    fetchHeldSales();
  }, []);

  const fetchHeldSales = async () => {
    try {
      const response = await axios.get(`${API}/sales/held`, getAuthHeader());
      setHeldSales(response.data);
    } catch (error) {
      toast.error("Failed to fetch held sales");
    } finally {
      setLoading(false);
    }
  };

  const handleResume = async (sale) => {
    // Clear current cart and add held sale items
    clearCart();
    for (const item of sale.items) {
      addToCart({
        id: item.product_id,
        name: item.name,
        barcode: item.barcode,
        price: item.price
      }, item.quantity);
    }
    
    // Delete the held sale
    try {
      await axios.delete(`${API}/sales/held/${sale.id}`, getAuthHeader());
      toast.success("Sale resumed - items added to cart");
      navigate("/");
    } catch (error) {
      toast.error("Failed to resume sale");
    }
  };

  const handleDelete = async () => {
    if (!selectedSale) return;
    
    try {
      await axios.delete(`${API}/sales/held/${selectedSale.id}`, getAuthHeader());
      toast.success("Held sale deleted");
      setShowDeleteModal(false);
      setSelectedSale(null);
      fetchHeldSales();
    } catch (error) {
      toast.error("Failed to delete held sale");
    }
  };

  const confirmDelete = (sale) => {
    setSelectedSale(sale);
    setShowDeleteModal(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="loading-spinner" />
      </div>
    );
  }

  return (
    <div className="animate-fade-in" data-testid="on-hold-sales-page">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
          <Clock className="w-6 h-6" />
          On Hold Sales
        </h1>
        <span className="text-slate-500">{heldSales.length} sales on hold</span>
      </div>

      {heldSales.length === 0 ? (
        <Card>
          <CardContent className="p-12 text-center">
            <ShoppingCart className="w-16 h-16 mx-auto text-slate-200 mb-4" />
            <p className="text-slate-500">No sales on hold</p>
            <p className="text-sm text-slate-400 mt-1">
              Save a sale without completing payment to see it here
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {heldSales.map((sale) => (
            <Card key={sale.id} className="hover:shadow-md transition-shadow">
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-3">
                  <span className="font-mono text-sm text-slate-500">
                    {sale.receipt_number}
                  </span>
                  <span className="px-2 py-1 bg-amber-100 text-amber-700 rounded text-xs font-bold">
                    ON HOLD
                  </span>
                </div>
                
                <div className="text-sm text-slate-500 mb-3">
                  {format(new Date(sale.created_at), "MMM dd, yyyy HH:mm")}
                </div>

                <div className="border-t border-b py-3 mb-3">
                  <p className="text-sm text-slate-600">
                    {sale.items.length} item(s)
                  </p>
                  <div className="mt-2 space-y-1">
                    {sale.items.slice(0, 3).map((item, index) => (
                      <p key={index} className="text-xs text-slate-500 truncate">
                        {item.quantity}x {item.name}
                      </p>
                    ))}
                    {sale.items.length > 3 && (
                      <p className="text-xs text-slate-400">
                        +{sale.items.length - 3} more items
                      </p>
                    )}
                  </div>
                </div>

                <div className="flex items-center justify-between mb-4">
                  <span className="text-slate-600">Total:</span>
                  <span className="text-xl font-bold text-blue-600">
                    ${sale.total.toFixed(2)}
                  </span>
                </div>

                <div className="flex gap-2">
                  <Button
                    onClick={() => handleResume(sale)}
                    className="flex-1 bg-green-600 hover:bg-green-700"
                    data-testid={`resume-sale-${sale.id}`}
                  >
                    <Play className="w-4 h-4 mr-1" />
                    Resume
                  </Button>
                  <Button
                    variant="destructive"
                    onClick={() => confirmDelete(sale)}
                    data-testid={`delete-sale-${sale.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Delete Confirmation Modal */}
      <Dialog open={showDeleteModal} onOpenChange={setShowDeleteModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Held Sale?</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p className="text-slate-600">
              Are you sure you want to delete this held sale? This action cannot be undone.
            </p>
            {selectedSale && (
              <div className="mt-4 p-4 bg-slate-50 rounded-lg">
                <p className="font-mono text-sm">{selectedSale.receipt_number}</p>
                <p className="font-bold text-lg">${selectedSale.total.toFixed(2)}</p>
              </div>
            )}
          </div>
          <div className="flex gap-3">
            <Button variant="outline" onClick={() => setShowDeleteModal(false)} className="flex-1">
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete} className="flex-1" data-testid="confirm-delete-btn">
              Delete Sale
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
